import {
  Button,
  Card,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  SvgIcon,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { RiDeleteBinLine } from '@remixicon/react';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import CustomChip from './CustomChip';
import FileUploader from './FileUploader';
import InputHeader from './InputHeader';
import { useCurrentUser } from '../contexts/currentUser';
import { useLanguage } from '../contexts/language';
import useForm from '../hooks/useForm';
import { useNavigationGuardBlocker } from '../hooks/useNavigationGuard';
import {
  AppFile,
  ContractInsuranceCoverageType,
  VesselInsuranceCoverageType,
} from '../types/core';
import {
  ContractInsurance,
  NewContractInsurance,
  NewVesselInsurance,
  VesselInsurance,
} from '../types/insurance';
import { Role } from '../types/user';
import {
  DAYJS_DATA_DATE_FORMAT,
  DAYJS_VISUALIZATION_DATE_FORMAT,
  getNullableDayjsDifferenceChecker,
} from '../utils/date';
import getMaterialInputsFormHelper from '../utils/getMaterialInputsFormHelper';
import { BADGE_CLASSNAMES } from '../utils/styles';

type BaseProps<TInsurance, TNewInsurance> = {
  insurance: TInsurance | null;
  isLoading: boolean;
  onDelete: (hasPendingChanges: boolean) => void;
  onSave: (insuranceValues: TNewInsurance) => void;
  areActionsDisabled?: boolean;
};

export type InsuranceCardElementProps =
  | ({ insuranceType: 'contract' } & BaseProps<
      ContractInsurance,
      NewContractInsurance
    >)
  | ({ insuranceType: 'vessel' } & BaseProps<
      VesselInsurance,
      NewVesselInsurance
    >);

type InsuranceFormValues = {
  coverageType: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  files: AppFile[];
};

const InsuranceCard = ({
  insuranceType,
  insurance,
  isLoading,
  onDelete,
  onSave,
  areActionsDisabled = false,
}: InsuranceCardElementProps) => {
  const { t } = useLanguage();
  const { hasRole } = useCurrentUser();

  const isAdmin = hasRole(Role.ADMIN);

  const initialValues = useMemo<InsuranceFormValues>(() => {
    if (!insurance) {
      return {
        coverageType: '',
        startDate: null,
        endDate: null,
        files: [],
      };
    }
    return {
      coverageType: insurance.coverageType,
      startDate: dayjs(insurance.startDate),
      endDate: dayjs(insurance.endDate),
      files: insurance.files,
    };
  }, [insurance]);

  const today = dayjs(new Date());

  const formState = useForm({
    initialValues,
    onSubmit: values => {
      if (insuranceType === 'contract') {
        onSave({
          coverageType: values.coverageType as ContractInsuranceCoverageType,
          startDate: values.startDate
            ? values.startDate.format(DAYJS_DATA_DATE_FORMAT)
            : '',
          endDate: values.endDate
            ? values.endDate.format(DAYJS_DATA_DATE_FORMAT)
            : '',
          files: values.files,
        });
      } else {
        onSave({
          coverageType: values.coverageType as VesselInsuranceCoverageType,
          startDate: values.startDate
            ? values.startDate.format(DAYJS_DATA_DATE_FORMAT)
            : '',
          endDate: values.endDate
            ? values.endDate.format(DAYJS_DATA_DATE_FORMAT)
            : '',
          files: values.files,
        });
      }
    },
    validate: (values, setError) => {
      if (!values.coverageType) {
        setError('coverageType', t('validation.required-field'));
      }
      if (!values.startDate) {
        setError('startDate', t('validation.required-field'));
      } else if (!values.startDate.isValid()) {
        setError('startDate', t('validation.invalid-date'));
      } else if (values.startDate.isAfter(today, 'day')) {
        setError(
          'startDate',
          t('validation.date-after-reference-date', {
            REFERENCE_DATE: today.format(DAYJS_VISUALIZATION_DATE_FORMAT),
          }),
        );
      }
      if (!values.endDate) {
        setError('endDate', t('validation.required-field'));
      } else if (!values.endDate.isValid()) {
        setError('endDate', t('validation.invalid-date'));
      } else if (
        values.startDate &&
        (values.startDate.isSame(values.endDate, 'day') ||
          values.startDate.isAfter(values.endDate, 'day'))
      ) {
        setError('endDate', t('validation.invalid-end-date'));
      }
      if (!values.files.length) {
        setError('files', t('validation.required-file'));
      }
    },
    differenceChecker: {
      startDate: getNullableDayjsDifferenceChecker('day'),
      endDate: getNullableDayjsDifferenceChecker('day'),
    },
    resetOnInitialValuesChange: () => !!insurance,
  });

  const {
    values,
    errors,
    updateValues,
    touchField,
    haveValuesChanged,
    handleSubmit,
  } = formState;

  useNavigationGuardBlocker(() => haveValuesChanged);

  // TODO: Enable edition
  const areInputsDisabled = isLoading || Boolean(insurance);

  const shouldShowDeleteButton = !insurance || isAdmin;

  const { getBaseProps, getSelectProps, renderError } =
    getMaterialInputsFormHelper(formState, {
      shouldInputsBeDisabled: areInputsDisabled,
    });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card variant="outlined">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <header className="col-span-full font-bold flex gap-4">
            <span>{t('content.insurance')}</span>
            <>
              {insurance ? (
                insurance.isExpired && (
                  <CustomChip
                    label={t(`content.expired`)}
                    className={BADGE_CLASSNAMES.RED}
                    small
                  />
                )
              ) : (
                <CustomChip small label={t('content.draft')} />
              )}
              {shouldShowDeleteButton && !areActionsDisabled && (
                <IconButton
                  disabled={isLoading}
                  onClick={() => onDelete(haveValuesChanged)}
                  className="ml-auto"
                >
                  <SvgIcon component={RiDeleteBinLine} />
                </IconButton>
              )}
            </>
          </header>
          <div className="flex flex-col lg:flex-row gap-4 justify-between">
            <label className="block md:w-[50%]">
              <FormControl error={getBaseProps('coverageType').error}>
                <InputHeader required>{t('content.coverage-type')}</InputHeader>
                <Select<string>
                  {...getSelectProps('coverageType')}
                  displayEmpty
                  renderValue={selected => {
                    if (!selected) {
                      return (
                        <span className="text-iots-grey-400">
                          {t('content.select')}
                        </span>
                      );
                    }
                    const baseKey =
                      insuranceType === 'contract'
                        ? 'insurance-coverage-types.contract'
                        : 'insurance-coverage-types.vessel';
                    return t(`${baseKey}.${selected}`);
                  }}
                >
                  {(insuranceType === 'contract'
                    ? Object.values(ContractInsuranceCoverageType)
                    : Object.values(VesselInsuranceCoverageType)
                  ).map(covType => (
                    <MenuItem key={covType} value={covType}>
                      {t(
                        `insurance-coverage-types.${
                          insuranceType === 'contract' ? 'contract' : 'vessel'
                        }.${covType}`,
                      )}
                    </MenuItem>
                  ))}
                </Select>
                {renderError('coverageType')}
              </FormControl>
            </label>
          </div>
          <div className="flex flex-col lg:flex-row gap-4">
            <label className="block w-full">
              <InputHeader required>{t('content.start-date')}</InputHeader>
              <FormControl error={Boolean(errors.startDate)}>
                <DatePicker
                  className="w-full"
                  format={DAYJS_VISUALIZATION_DATE_FORMAT}
                  slotProps={{
                    textField: {
                      error: Boolean(errors.startDate),
                      onBlur: () => touchField('startDate'),
                      disabled: areInputsDisabled,
                    },
                  }}
                  value={values.startDate}
                  maxDate={
                    values.endDate && values.endDate.isBefore(today)
                      ? values.endDate
                      : today
                  }
                  onChange={newValue =>
                    updateValues({
                      startDate: newValue,
                    })
                  }
                  disabled={areInputsDisabled}
                />
                {errors.startDate && (
                  <FormHelperText>{errors.startDate}</FormHelperText>
                )}
              </FormControl>
            </label>
            <label className="block w-full">
              <InputHeader required>{t('content.end-date')}</InputHeader>
              <FormControl error={Boolean(errors.endDate)}>
                <DatePicker
                  className="w-full"
                  format={DAYJS_VISUALIZATION_DATE_FORMAT}
                  slotProps={{
                    textField: {
                      error: Boolean(errors.endDate),
                      onBlur: () => touchField('endDate'),
                    },
                  }}
                  value={values.endDate}
                  minDate={values.startDate ?? today}
                  onChange={newValue =>
                    updateValues({
                      endDate: newValue,
                    })
                  }
                  disabled={areInputsDisabled}
                />
                {errors.endDate && (
                  <FormHelperText>{errors.endDate}</FormHelperText>
                )}
              </FormControl>
            </label>
          </div>
          <div>
            <InputHeader required>{t('content.files')}</InputHeader>
            <span className="text-iots-grey-500 text-sm">
              {t('content.files-accepted-formats')}
            </span>
            <FormControl error={Boolean(errors.files)}>
              <FileUploader
                isError={Boolean(errors.files)}
                onChange={(validFiles: AppFile[]) => {
                  updateValues({
                    files: [...values.files, ...validFiles],
                  });
                }}
                onDelete={(id: string) => {
                  updateValues({
                    files: values.files.filter(file => file.id !== id),
                  });
                }}
                files={values.files}
                disabled={areInputsDisabled}
              />
              {errors.files && <FormHelperText>{errors.files}</FormHelperText>}
            </FormControl>
          </div>
          <div className="flex justify-end">
            <Button
              variant="contained"
              type="submit"
              disabled={
                !haveValuesChanged ||
                areInputsDisabled ||
                Object.values(errors).length > 0
              }
            >
              {t('content.save')}
            </Button>
          </div>
        </form>
      </Card>
    </LocalizationProvider>
  );
};

export default InsuranceCard;
